import type { PropsWithChildren } from 'react';
import clsx from 'clsx';
import { noop } from 'lodash';
import { Button2, Separator } from '@frameio/vapor';
import TEXT from './generated/TEXT/BillingStripeElementContainer';
import styles from './StripeElementContainer.module.css';

TEXT.DEFINE({
  /** Button text to cancel payment method changes */
  cancel: 'Cancel',
  /** Button text for confirming payment method changes. Shows "Continue to Checkout" when a payment method exists, "Save" when adding a new one. */
  confirm: (hasPaymentMethod: boolean): string =>
    hasPaymentMethod ? 'Continue to Checkout' : 'Save',
});

const notVisibleStyle = {
  display: 'none',
};

const visibleStyle = {
  display: 'block',
};

type StripeElementContainerProps = PropsWithChildren<{
  isSubmitDisabled: boolean;
  isVisible: boolean;
  onCancel?: () => void;
  onSubmit?: () => void;
  hasPaymentMethod?: boolean;
  isCollapsed?: boolean;
}>;

export default function StripeElementContainer({
  children,
  isSubmitDisabled = true,
  isVisible = false,
  onCancel = noop,
  onSubmit = noop,
  hasPaymentMethod = false,
  isCollapsed = true,
}: StripeElementContainerProps) {
  /*
      Stripe Elements come with thier own animations that we can't disable.
      These animations trigger on mount of the component. If we use CSS display
      to show/hide the components, we can avoid these animations.
    */
  return (
    <div style={isVisible ? visibleStyle : notVisibleStyle}>
      {children}

      {!isCollapsed && (
        <div className={styles.separatorWrapper}>
          <Separator />
        </div>
      )}

      <div
        className={clsx(
          styles.buttonContainer,
          isCollapsed && styles.collapsed
        )}
      >
        <Button2
          onPress={onCancel}
          size={isCollapsed ? 'small' : undefined}
          tabIndex={0}
        >
          {TEXT.cancel}
        </Button2>
        <Button2
          data-testid="payment-method-confirmation-button"
          isDisabled={isSubmitDisabled}
          onPress={onSubmit}
          size={isCollapsed ? 'small' : undefined}
          tabIndex={0}
          variant="primary"
        >
          {TEXT.confirm(hasPaymentMethod)}
        </Button2>
      </div>
    </div>
  );
}
